import { useMemo } from "react";
import Modal from "../../layouts/Modal";
import { IInvoice, InvoiceStatus } from "../../models/invoice/IInvoice";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";
import InvoiceItemsTable from "./InvoiceItemsTable";
import { useForm } from "react-hook-form";
import CreditInvoiceItemsTable from "./CreditInvoiceItemsTable";
import useApiHelper from "../../hooks/useApiHelper";
import { useMutation } from "@tanstack/react-query";
import apiConfig from "../../utils/apiConfig";


interface InvoiceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoice: IInvoice | null;
}

type CreditInvoiceItemPayload = {
  invoiceItemId: string;   // Guid string
  amount: number;
  quantity: number;
  scrap: boolean;
  returnToStock: boolean;
};

type CreditInvoiceRequestPayload = {
  invoiceId: string;       // Guid string
  items: CreditInvoiceItemPayload[];
};

const toNumber = (v: any, d = 0) => {
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : d;
};
const toInt = (v: any, d = 0) => {
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : d;
};
const toBool = (v: any) => v === true || v === "true" || v === 1 || v === "1";

export default function CreditInvoiceModal({ open, setOpen, invoice }: InvoiceModalProps) {
  const { register, setValue, getValues, watch, reset, handleSubmit } = useForm();
  const { post } = useApiHelper(apiConfig("CreditInvoiceModal"));

  const creditInvoiceMutation = useMutation({
    mutationFn: (payload: CreditInvoiceRequestPayload) =>
      post(`/invoice/${payload.invoiceId}/credit`, payload),
  });

  const isEmpty = useMemo(() => {
    return invoice?.items && invoice.items.length === 0;
  }, [invoice]);

  const onSubmit = async (data: any) => {
    if (!invoice) return;

    const items: CreditInvoiceItemPayload[] = (invoice.items ?? []).reduce(
      (acc: CreditInvoiceItemPayload[], item) => {
        const credited = toBool(data[`credit-${item.id}`]);
        if (!credited) return acc;

        const quantity = toInt(data[`quantity-${item.id}`], item.quantity ?? 0);
        const amount   = toNumber(data[`amount-${item.id}`], 0);
        const returnToStock      = toBool(data[`return-${item.id}`]);
        const scrap    = toBool(data[`scrap-${item.id}`]);

        // Only include sensible rows
        if (quantity > 0 && amount >= 0) {
          acc.push({
            invoiceItemId: item.id,
            quantity,
            amount,
            scrap,
            returnToStock
          });
        }
        return acc;
      },
      []
    );

    const payload: CreditInvoiceRequestPayload = {
      invoiceId: invoice.id,
      items,
    };

    if (payload.items.length === 0) {
      // You can swap this for your toast system
      alert("Select at least one item to credit.");
      return;
    }

    try {
      await creditInvoiceMutation.mutateAsync(payload);
      // Close modal (and optionally refresh invoice list outside)
      setOpen(false);
    } catch (err) {
      console.error("Credit invoice failed", err);
      alert("There was a problem crediting the invoice.");
    }
  };

  if (!invoice) return (<Modal open={open} setOpen={setOpen} width="sm:max-w-2xl"><LoadingWheel /></Modal>);

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-5xl">
      <>
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Credit Invoice #{invoice.invoiceNumber} : <span className="text-red-700">{invoice.reference}</span></h3>
        <p className="text-sm text-gray-500 mb-4">Leave return and scrap blank if they are not returning item/ just providing a partial refund</p>
        {isEmpty ? (
          <p className="mt-8 text-center text-xl text-gray-700">No items attached to this invoice</p>
        ) :
          <form onSubmit={handleSubmit(onSubmit)}>
            <CreditInvoiceItemsTable
              data={invoice?.items ?? []}
              register={register}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
            />

            <div className="flex justify-between mt-10">
              <ButtonInput
                isSubmit={false}
                onClick={() => setOpen(false)}
                label="Close"
                classes="!bg-gray-200 hover:!bg-gray-300 !text-gray-800"
              />

              <ButtonInput
                isSubmit={true}
                onClick={() => {}}
                label="Credit Invoice"
                classes=""
              />
            </div>
          </form>
        }

      </>
    </Modal>
  );
}
